/**
 * Componente FormField reutilizable
 * Campo de formulario con icono, label, error y soporte para input/select/textarea
 */

import React from 'react';
import PropTypes from 'prop-types';

const FormField = ({
  label,
  id,
  name,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  autoComplete,
  options = [],
  error,
  icon: Icon,
  rows = 4,
  maxLength,
  className = ''
}) => {
  const inputName = name || id;
  
  const baseInputClasses = `
    w-full p-3 border-2 rounded-lg 
    focus:outline-none focus:ring-2 
    transition-colors duration-200
    disabled:bg-gray-100 disabled:cursor-not-allowed
  `;
  
  const inputClasses = `
    ${baseInputClasses}
    ${Icon ? 'pl-10' : ''}
    ${error 
      ? 'border-red-500 focus:ring-red-300 focus:border-red-500' 
      : 'border-gray-300 focus:ring-blue-400 focus:border-blue-500'
    }
  `;

  const renderInput = () => {
    const commonProps = {
      id,
      name: inputName,
      value,
      onChange,
      disabled,
      required,
      autoComplete,
      className: inputClasses,
      'aria-invalid': error ? 'true' : 'false',
      'aria-describedby': error ? `${id}-error` : undefined
    };

    switch (type) {
      case 'select':
        return (
          <select {...commonProps}>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            {...commonProps}
            placeholder={placeholder}
            rows={rows}
            maxLength={maxLength}
            className={`${inputClasses} resize-none`}
          />
        );

      default:
        return (
          <input
            {...commonProps}
            type={type}
            placeholder={placeholder}
            maxLength={maxLength}
          />
        );
    }
  };

  return (
    <div className={`mb-4 ${className}`}>
      {label && (
        <label 
          htmlFor={id} 
          className="block text-gray-700 font-semibold mb-2"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500">
            <Icon className="w-5 h-5" />
          </div>
        )}
        {renderInput()}
      </div>
      
      {error && (
        <p 
          id={`${id}-error`}
          className="text-red-500 text-sm mt-1"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};

FormField.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  type: PropTypes.oneOf(['text', 'email', 'tel', 'password', 'number', 'select', 'textarea']),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  autoComplete: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  })),
  error: PropTypes.string,
  icon: PropTypes.elementType,
  rows: PropTypes.number,
  maxLength: PropTypes.number,
  className: PropTypes.string
};

export default FormField;
